import { z } from 'zod';
import { articleUnitSchema } from './article.schema';

/**
 * Zusatzmaterial am Artikel („Artikel-Kit", VB-26016, ADR 0088).
 *
 * Vormals `production/bom.schema.ts` („Stücklisten-Position", ADR 0008). Die
 * Tabelle bleibt dieselbe, die Fachlichkeit ist eine andere: Wir fertigen nicht
 * (ADR 0009), sondern hinterlegen, was zu einem Artikel ÜBLICHERWEISE mitgeht —
 * Befestigungs- und Zusatzmaterial. Deshalb liegt es jetzt beim Artikelstamm.
 */

/** Eine Zusatzmaterial-Zeile am Artikel. */
export const kitItemResponseSchema = z.object({
  id: z.string().uuid(),
  /** Hauptartikel, an dem die Zeile hängt. */
  parentArticleId: z.string().uuid(),
  articleId: z.string().uuid(),
  articleNumber: z.string(),
  articleName: z.string(),
  unit: articleUnitSchema.nullable(),
  /** Stückzahl je EINER Einheit des Hauptartikels. */
  quantityPerUnit: z.number(),
  position: z.number().int(),
  note: z.string().nullable(),
  /** Hat dieses Zusatzmaterial selbst wieder Zusatzmaterial? (Anzeige-Hinweis) */
  hasOwnKit: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type KitItemResponse = z.infer<typeof kitItemResponseSchema>;

export const createKitItemSchema = z.object({
  articleId: z.string().uuid('Bitte einen Artikel wählen.'),
  quantityPerUnit: z.number().positive('Stückzahl je Einheit muss größer als 0 sein.'),
  note: z.string().trim().max(500).optional(),
});
export type CreateKitItemInput = z.infer<typeof createKitItemSchema>;

export const updateKitItemSchema = z.object({
  quantityPerUnit: z.number().positive('Stückzahl je Einheit muss größer als 0 sein.').optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type UpdateKitItemInput = z.infer<typeof updateKitItemSchema>;

/**
 * Standard des Artikels: Kommt das Zusatzmaterial beim Einfügen normalerweise
 * mit? Bleibt bei jeder einzelnen Erfassung übersteuerbar.
 */
export const updateKitDefaultSchema = z.object({ kitDefaultOn: z.boolean() });
export type UpdateKitDefaultInput = z.infer<typeof updateKitDefaultSchema>;

/** Eine aufgelöste, fertig skalierte Zeile (Vorschau bzw. Einfüge-Vorlage). */
export const kitLineResponseSchema = z.object({
  articleId: z.string().uuid(),
  articleNumber: z.string(),
  articleName: z.string(),
  unit: articleUnitSchema.nullable(),
  quantity: z.number(),
  /** 1 = direktes Zusatzmaterial, 2 = dessen Zusatzmaterial … */
  depth: z.number().int().min(1),
  /** Artikelnummer des Hauptartikels dieser Zeile. */
  parentNumber: z.string(),
});
export type KitLineResponse = z.infer<typeof kitLineResponseSchema>;

export const kitResolutionResponseSchema = z.object({
  lines: z.array(kitLineResponseSchema),
  /** Wegen Ringbezug abgebrochene Ketten — werden gemeldet, nicht verschwiegen. */
  cycles: z.array(z.object({ articleNumber: z.string(), path: z.array(z.string()) })),
  truncated: z.boolean(),
});
export type KitResolutionResponse = z.infer<typeof kitResolutionResponseSchema>;

/**
 * Eine Zeile der Übersicht „Zusatzmaterial" (Technik): welche Artikel überhaupt
 * Zusatzmaterial hinterlegt haben. Ohne diese Seite findet man ein Kit nur, wenn
 * man den Artikel schon kennt — und genau das war die Bitte aus der Meldung.
 */
export const kitOverviewRowSchema = z.object({
  articleId: z.string().uuid(),
  articleNumber: z.string(),
  articleName: z.string(),
  unit: articleUnitSchema.nullable(),
  /** Zeilen auf der ERSTEN Stufe. */
  directCount: z.number().int(),
  /** Zeilen über alle Stufen für Menge 1 — zeigt, wie tief das Kit wirklich geht. */
  resolvedCount: z.number().int(),
  /** Kommt das Zusatzmaterial beim Einfügen normalerweise mit? */
  kitDefaultOn: z.boolean(),
  /** Ringbezug in der Kette — dann stimmt die Auflösung nicht mehr vollständig. */
  hasCycle: z.boolean(),
});
export type KitOverviewRow = z.infer<typeof kitOverviewRowSchema>;

/** Abfrage der Auflösung: auf welche Menge des Hauptartikels skaliert wird. */
export const resolveKitQuerySchema = z.object({
  quantity: z.coerce.number().positive().default(1),
});
export type ResolveKitQuery = z.infer<typeof resolveKitQuerySchema>;
